import Highcharts, { Options } from 'highcharts';
import { PieChartType } from 'src/api/api-types/diagrams';
import {
  pieColors,
  contextButtonTheme,
  PIE_CHART_SIZE_DEFAULT,
  CHART_HEIGHT,
  pieChartLegendConfig,
  pieChartResponsiveConfig,
  chartLoadingConfig,
  chartTitleStyle,
  pieDataLabelsStyle,
} from '../../pages/Dashboard/components/Charts/config/constant';
import { ClickOnLegendType, ExportCallBackType } from '../../types';
import { exportOptions } from '../../pages/Dashboard/helpers/common';
import HighchartsReact from 'highcharts-react-official';
import {
  PrintableChart,
  downloadPdf,
  getPieChartExportRasterImageProps,
  getPieChartExportSVGProps,
  formatPieDataLabel,
  applyMinSliceAngle,
} from './utils';
import { ExportFileType } from 'src/api/exportTypes';

export class PieChart extends PrintableChart {
  protected exportCallBack?: ExportCallBackType;
  protected clickOnSection?: ClickOnLegendType;
  protected customColors?: Record<string, string>;
  public options: Options;
  public chartRef?: HighchartsReact.Props['pie'];
  constructor(
    pieChart: PieChartType,
    callBackClick: ClickOnLegendType,
    exportCallBack: ExportCallBackType,
    customColors?: Record<string, string>,
  ) {
    const options = getPieChartExportRasterImageProps('application/pdf');
    super(
      options.exportOptions.sourceWidth,
      options.exportOptions.sourceHeight,
      options.exportOptions.scale,
    );

    this.clickOnSection = callBackClick;
    this.exportCallBack = exportCallBack;
    this.customColors = customColors;
    this.options = {
      chart: {
        type: 'pie',
        height: CHART_HEIGHT,
      },
      loading: chartLoadingConfig,
      title: {
        text: pieChart.title,
        align: 'left',
        y: 24,
        widthAdjust: -100,
        style: chartTitleStyle,
      },
      series: [
        {
          type: 'pie',
          name: pieChart.title,
          data: this.formatToSeries(pieChart, customColors),
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter: function (this: Highcharts.PointLabelObject) {
              const originalY = (this.point as any).originalY;
              return formatPieDataLabel(
                this.point.name,
                this.point.y,
                originalY,
              );
            },
            style: pieDataLabelsStyle,
          },
          tooltip: {
            headerFormat: '',
            pointFormatter: function () {
              const originalY = (this as any).originalY;
              const displayValue = originalY !== undefined ? originalY : this.y;
              return `${this.name}: ${displayValue}`;
            },
          },
        },
      ] as Highcharts.SeriesOptionsType[],
      legend: pieChartLegendConfig,
      responsive: pieChartResponsiveConfig,
      plotOptions: {
        pie: {
          size: PIE_CHART_SIZE_DEFAULT,
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter: function (this: Highcharts.PointLabelObject) {
              const originalY = (this.point as any).originalY;
              return formatPieDataLabel(
                this.point.name,
                this.point.y,
                originalY,
              );
            },
            connectorWidth: 2,
            alignTo: 'plotEdges',
            connectorShape: 'crookedLine',
            crookDistance: 70,
            padding: 5,
          },
        },
        series: {
          showInNavigator: true,
          point: {
            events: {
              click: this.pointClick.bind(this),
            },
          },
        },
      },
      exporting: {
        chartOptions: {
          legend: {
            useHTML: true,
            labelFormatter: function () {
              const v = (this as any).originalY ?? (this as any).y;
              return `${this.name}: <span style="font-size: 1.1em;">${v}</span>`;
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                enabled: true,
              },
            },
          },
        },
        buttons: {
          contextButton: {
            ...contextButtonTheme,
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXSLS',
            ],
          },
        },
        menuItemDefinitions: {
          downloadXSLS: {
            onclick: () => {
              if (!this.exportCallBack) return;
              this.chartRef.fullscreen.isOpen && this.chartRef.showLoading();
              this.exportCallBack({
                dashboard: [
                  exportOptions['SLCP EAR Collection Systems Pie Chart'],
                ],
                type: ExportFileType.XLSX,
              });
            },
            text: 'Download XSLS',
          },
          downloadSVG: {
            onclick: () => {
              if (!this.chartRef) return;
              const options = getPieChartExportSVGProps('image/svg+xml');
              this.chartRef.exportChartLocal(
                options.exportOptions,
                options.chartOptions,
              );
            },
          },
          downloadCSV: {
            onclick: () => {
              if (!this.exportCallBack) return;
              this.chartRef.fullscreen.isOpen && this.chartRef.showLoading();
              this.exportCallBack({
                dashboard: [
                  exportOptions['SLCP EAR Collection Systems Pie Chart'],
                ],
                type: ExportFileType.CSV,
              });
            },
            text: 'Download CSV',
          },
          downloadPNG: {
            onclick: () => {
              if (!this.chartRef) return;
              const options = getPieChartExportRasterImageProps('image/png');
              this.chartRef.exportChartLocal(
                options.exportOptions,
                options.chartOptions,
              );
            },
            text: 'Download PNG image',
          },
          downloadJPEG: {
            onclick: () => {
              if (!this.chartRef) return;
              const options = getPieChartExportRasterImageProps('image/jpeg');
              this.chartRef.exportChartLocal(
                options.exportOptions,
                options.chartOptions,
              );
            },
            text: 'Download JPEG image',
          },
          printChart: {
            onclick: () => {
              if (!this.chartRef || !this.onPrint) return;
              this.onPrint();
            },
            text: 'Print chart',
          },
          downloadPDF: {
            onclick: () => {
              if (!this.chartRef) return;
              const options =
                getPieChartExportRasterImageProps('application/pdf');
              downloadPdf(
                this.chartRef,
                options.exportOptions.sourceWidth,
                options.exportOptions.sourceHeight,
              );
            },
            text: 'Download PDF document',
          },
        },
        fallbackToExportServer: false,
      },
    };
  }

  protected formatToSeries(data: PieChartType, colors?: Record<string, string>) {
    const colorMap = colors || pieColors;
    const rawData = data.values
      .filter(item => Number(item.value) > 0)
      .map((item) => ({
        id: !!item.filter?.id ? item.filter.id : `0`,
        name: item.title,
        y: Number(item.value),
        color: colorMap[item.guid],
      }));
    return applyMinSliceAngle(rawData);
  }

  private pointClick(
    data: Highcharts.PointClickEventObject,
    resetSelection: boolean = false,
  ) {
    if (resetSelection) {
      data.point.series.points.forEach(point => {
        point.setState('');
      });
    }
    if (!this.clickOnSection || data.point.selected) return;
    const originalY = (data.point as any).originalY;
    this.clickOnSection(
      (data.point as Highcharts.Point & { id: string }).id,
      data.point.name,
      data.point.series.name,
      originalY ?? data.point.y,
    );
  }

  public setChartRef(data: HighchartsReact.Props['pie']) {
    this.chartRef = data;
  }

  public resetChartRef() {
    this.chartRef = undefined;
  }

  public resetSelection() {
    if (this.chartRef === undefined) {
      // eslint-disable-next-line no-console
      console.error("Chart red is empty, can't reset selection");
      return;
    }
    this.chartRef.series[0].points.forEach((point: Highcharts.Point) => {
      point.setState('');
      if (point.sliced) {
        point.sliced = false;
        point.select(false);
      }
    });
  }
}
